import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { validateIndicatorStructure } from '@/utils/indicatorUtils';

interface Indicator {
  id: number;
  level1_name: string;
  level2_name: string;
  indicator_name: string;
  weight: number;
  created_at: string;
  updated_at: string;
}

interface WeightAdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  indicators: Indicator[];
  onSave: (adjustedIndicators: Indicator[]) => Promise<void>;
  error?: string;
  clearError?: () => void;
}

const WeightAdjustmentDialog: React.FC<WeightAdjustmentDialogProps> = ({
  open,
  onClose,
  indicators,
  onSave,
  error,
  clearError
}) => {
  const [adjustedIndicators, setAdjustedIndicators] = useState<Indicator[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validation, setValidation] = useState<{ isValid: boolean; issues: string[] }>({ isValid: true, issues: [] });

  useEffect(() => {
    if (open && indicators.length > 0) {
      // 直接使用原始指标数据，不自动调整权重
      const originalIndicators = indicators.map(ind => ({ ...ind }));
      setAdjustedIndicators(originalIndicators);
      
      // 验证权重结构
      const validationResult = validateIndicatorStructure(originalIndicators);
      setValidation(validationResult);
    }
  }, [open, indicators]);

  const handleWeightChange = (indicatorId: number, newWeight: number) => {
    const updated = adjustedIndicators.map(ind => 
      ind.id === indicatorId ? { ...ind, weight: newWeight } : ind
    );
    setAdjustedIndicators(updated);
    
    // 重新验证
    const validationResult = validateIndicatorStructure(updated);
    setValidation(validationResult);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(adjustedIndicators);
      onClose();
    } catch (err) {
    } finally {
      setIsSaving(false);
    }
  };


  // 按一级指标分组
  const groupedIndicators = adjustedIndicators.reduce((acc, indicator) => {
    const level1 = indicator.level1_name;
    if (!acc[level1]) {
      acc[level1] = [];
    }
    acc[level1].push(indicator);
    return acc;
  }, {} as Record<string, Indicator[]>);

  // 计算总权重和各一级指标权重
  const totalWeight = adjustedIndicators.reduce((sum, ind) => sum + ind.weight, 0);
  
  // 计算各一级指标的权重分布
  const level1Weights = Object.entries(groupedIndicators).map(([level1, indicators]) => ({
    level1,
    weight: indicators.reduce((sum, ind) => sum + ind.weight, 0),
    count: indicators.length
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">权重调整</Typography>
          </Box>
          
          {/* 固定在顶部的权重概览卡片 */}
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5',
              borderRadius: 2
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography 
                variant="h6" 
                color={Math.abs(totalWeight - 100) < 0.01 ? "success.main" : "error.main"}
              >
                总权重: {totalWeight.toFixed(1)} / 100
              </Typography>
              {Math.abs(totalWeight - 100) >= 0.01 && (
                <Typography variant="body2" color="error" sx={{ ml: 2 }}>
                  总权重必须等于100才能保存
                </Typography>
              )}
            </Box>
            
            <Box display="flex" gap={1} flexWrap="wrap">
              {level1Weights.map(({ level1, weight }) => (
                <Chip
                  key={level1}
                  label={`${level1}: ${weight.toFixed(1)}%`}
                  variant="outlined"
                  color="primary"
                  sx={{
                    fontWeight: 'bold',
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                    }
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {!validation.isValid && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>权重结构问题:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validation.issues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Stack spacing={2}>
          {Object.entries(groupedIndicators).map(([level1_name, l1_indicators]) => {
            const level1Weight = l1_indicators.reduce((sum, ind) => sum + ind.weight, 0);
            
            return (
              <Accordion key={level1_name} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography variant="subtitle1" fontWeight="bold">
                      {level1_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      权重: {level1Weight.toFixed(1)}% ({l1_indicators.length}个指标)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>二级指标</TableCell>
                          <TableCell>三级指标</TableCell>
                          <TableCell align="right" sx={{ width: '120px' }}>权重 (%)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {l1_indicators.map(indicator => (
                          <TableRow key={indicator.id}>
                            <TableCell>{indicator.level2_name}</TableCell>
                            <TableCell>{indicator.indicator_name}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                value={indicator.weight}
                                onChange={(e) => handleWeightChange(indicator.id, e.target.value ? Number(e.target.value) : 0)}
                                inputProps={{ step: 0.5, min: 0 }}
                                sx={{ width: '80px' }}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          取消
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!validation.isValid || isSaving}
          startIcon={isSaving ? <CircularProgress size={20}/> : <CheckCircleOutlineIcon />}
        >
          {isSaving ? '正在保存...' : '确认调整'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeightAdjustmentDialog;
